import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import financeService from '../../../services/financeService';
import { ACCOUNT_TYPE_LABEL, LedgerAccount } from '../../../types/finance';
import SearchableSelect from '../../common/SearchableSelect';

/** Children sit under their group's code prefix: 52xx under 5200, 112x under 1120. */
const prefixOf = (groupCode: string) => groupCode.replace(/0+$/, '');

/** The first free code under a group, e.g. 5250 under 5200. */
const suggestCode = (group: LedgerAccount, accounts: LedgerAccount[]): string => {
  const prefix = prefixOf(group.code);
  const used = new Set(accounts.map(a => a.code));
  const width = 4 - prefix.length;
  const step = width >= 2 ? 10 : 1;
  for (let n = step; n < 10 ** width; n += step) {
    const code = prefix + String(n).padStart(width, '0');
    if (!used.has(code)) return code;
  }
  return '';
};

/**
 * Add an account under a group, or rename one. Code and type are fixed
 * once an account exists — the type comes from its group.
 */
const AccountDialog: React.FC<{
  open: boolean;
  account: LedgerAccount | null;
  accounts: LedgerAccount[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, account, accounts, onClose, onSaved }) => {
  const groups = useMemo(() => accounts.filter(a => a.isGroup), [accounts]);
  const [parentCode, setParentCode] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setParentCode(account?.parentCode ?? '');
    setCode(account?.code ?? '');
    setName(account?.name ?? '');
    setDescription(account?.description ?? '');
    setError(null);
  }, [open, account]);

  const parent = groups.find(g => g.code === parentCode);
  const pickGroup = (c: string) => {
    setParentCode(c);
    const g = groups.find(x => x.code === c);
    setCode(g ? suggestCode(g, accounts) : '');
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (account) await financeService.updateAccount(account._id, { name, description: description || null });
      else await financeService.createAccount({ parentCode, code, name, description: description || null });
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'The account could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{account ? 'Edit account' : 'Add account'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {account ? (
            <Typography variant="body2" color="text.secondary">
              <strong style={{ fontFamily: 'monospace' }}>{account.code}</strong> · {ACCOUNT_TYPE_LABEL[account.type]}. The code and type stay fixed.
            </Typography>
          ) : (
            <>
              <SearchableSelect
                label="Under"
                value={parentCode}
                onChange={pickGroup}
                options={groups.map(g => ({ value: g.code, label: `${g.code} · ${g.name}`, secondary: ACCOUNT_TYPE_LABEL[g.type] }))}
                placeholder="Choose a group"
                fullWidth
                clearable={false}
              />
              <TextField
                label="Code"
                size="small"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                disabled={!parent}
                helperText={parent ? `Four digits starting with ${prefixOf(parent.code)}` : 'Choose a group first'}
                inputProps={{ inputMode: 'numeric', style: { fontFamily: 'monospace' } }}
              />
            </>
          )}
          <TextField label="Name" size="small" value={name} onChange={e => setName(e.target.value)} inputProps={{ maxLength: 120 }} autoFocus={!!account} />
          <TextField
            label="Description"
            size="small"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What lands in this account (optional)"
            inputProps={{ maxLength: 300 }}
            multiline
            minRows={2}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || !name.trim() || (!account && (!parent || code.length !== 4))}>
          {busy ? 'Saving…' : account ? 'Save' : 'Add account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountDialog;
